"use client";

import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import { GlassIconButton } from "@/components/design-system/buttons/GlassIconButton";
import { ArtistIcon } from "@/components/design-system/icons/ArtistIcon";
import { useArtistRuntimeConfiguration } from "@/components/runtime/ArtistRuntimeConfigurationProvider";
import { artistPublicTrackURL } from "@/domain/auth/AuthProviderConfiguration";
import {
	browserDiscoveryRepository,
	type DiscoveryTrack,
} from "@/domain/discovery/DiscoveryRepository";
import {
	listLocalTrackAssets,
	type LocalTrackAsset,
} from "@/domain/media/LocalTrackAssetStore";
import { useTranslation } from "@/i18n/ArtistI18n";
import Link from "@/infrastructure/router/Link";
import { useRouter, useSearchParams } from "@/infrastructure/router/navigation";
import type { Route } from "@/infrastructure/router/types";

interface PlayerVisual {
	readonly id: string;
	readonly mediaType: "image" | "video";
	readonly url: string;
	readonly width: number | null;
	readonly height: number | null;
}

interface LocalPlayerMedia {
	readonly kind: "local";
	readonly asset: LocalTrackAsset;
	readonly audioURL: string;
	readonly artworkURL: string | null;
	readonly visuals: readonly PlayerVisual[];
}

interface PublicPlayerMedia {
	readonly kind: "public";
	readonly track: DiscoveryTrack;
	readonly visuals: readonly PlayerVisual[];
}

type PlayerMedia = LocalPlayerMedia | PublicPlayerMedia;

type PlayerQueueItem =
	| { readonly kind: "local"; readonly id: string; readonly title: string }
	| { readonly kind: "public"; readonly id: string; readonly title: string };

export function GenerationPlayer({
	publicTrackId: routePublicTrackId,
}: Readonly<{ publicTrackId?: string }> = {}) {
	const auth = useArtistSession();
	const runtimeConfiguration = useArtistRuntimeConfiguration();
	const { t } = useTranslation();
	const router = useRouter();
	const search = useSearchParams();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const queueDialogRef = useRef<HTMLDialogElement | null>(null);
	const infoDialogRef = useRef<HTMLDialogElement | null>(null);
	const resumeAfterLoadRef = useRef(false);
	const localTrackId = search.get("id") ?? search.get("request");
	const publicTrackId = routePublicTrackId ?? search.get("remote");
	const [media, setMedia] = useState<PlayerMedia | null | undefined>(undefined);
	const [queue, setQueue] = useState<readonly PlayerQueueItem[]>([]);
	const [selectedVisualIndex, setSelectedVisualIndex] = useState(0);
	const [displayMode, setDisplayMode] = useState<"fill" | "fit">("fill");
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [audioError, setAudioError] = useState(false);
	const [showLyrics, setShowLyrics] = useState(false);
	const [shareStatus, setShareStatus] = useState<string | null>(null);

	useEffect(() => {
		let active = true;
		const objectURLs: string[] = [];
		setMedia(undefined);
		setQueue([]);
		if (publicTrackId !== null) {
			Promise.all([
				browserDiscoveryRepository.loadPublicTrack(publicTrackId),
				browserDiscoveryRepository.loadPublicQueue(),
			])
				.then(([track, tracks]) => {
					if (!active) return;
					if (track === null || !track.playbackURL) {
						setMedia(null);
						return;
					}
					setMedia({
						kind: "public",
						track,
						visuals: (track.continents ?? []).map((continent) => ({
							id: continent.id,
							mediaType: continent.mediaType,
							url: continent.url,
							width: continent.width,
							height: continent.height,
						})),
					});
					setQueue(
						tracks
							.filter((item) => Boolean(item.playbackURL))
							.map((item) => ({
								kind: "public",
								id: item.id,
								title: item.title,
							})),
					);
				})
				.catch(() => {
					if (active) setMedia(null);
				});
			return () => {
				active = false;
			};
		}
		if (auth.phase !== "user" || auth.scope === null || localTrackId === null) {
			setMedia(null);
			return;
		}
		const scope = auth.scope;
		listLocalTrackAssets(scope)
			.then((assets) => {
				if (!active) return;
				setQueue(
					assets.map((asset) => ({
						kind: "local",
						id: asset.id,
						title: asset.title,
					})),
				);
				const asset =
					assets.find((candidate) => candidate.id === localTrackId) ?? null;
				if (asset === null) {
					setMedia(null);
					return;
				}
				const audioURL = URL.createObjectURL(asset.audioBlob);
				objectURLs.push(audioURL);
				const artworkURL =
					asset.artworkBlob === null
						? null
						: URL.createObjectURL(asset.artworkBlob);
				if (artworkURL !== null) objectURLs.push(artworkURL);
				const visuals = asset.continents.map((continent) => {
					const url = URL.createObjectURL(continent.blob);
					objectURLs.push(url);
					return {
						id: continent.id,
						mediaType: continent.mediaType,
						url,
						width: null,
						height: null,
					} satisfies PlayerVisual;
				});
				setMedia({ kind: "local", asset, audioURL, artworkURL, visuals });
			})
			.catch(() => {
				if (active) setMedia(null);
			});
		return () => {
			active = false;
			for (const url of objectURLs) URL.revokeObjectURL(url);
		};
	}, [auth.phase, auth.scope, localTrackId, publicTrackId]);

	const currentId =
		media == null
			? null
			: media.kind === "local"
				? media.asset.id
				: media.track.id;
	const currentIndex =
		currentId === null ? -1 : queue.findIndex((item) => item.id === currentId);
	const previous = currentIndex > 0 ? queue[currentIndex - 1] : undefined;
	const next =
		currentIndex >= 0 && currentIndex < queue.length - 1
			? queue[currentIndex + 1]
			: undefined;
	const title =
		media == null
			? ""
			: media.kind === "local"
				? media.asset.title || media.asset.audioFilename
				: media.track.title;
	const artist =
		media == null
			? ""
			: media.kind === "local"
				? t("player.you")
				: media.track.artist;
	const artworkURL =
		media == null
			? null
			: media.kind === "local"
				? media.artworkURL
				: (media.track.artworkURL ?? null);
	const audioURL =
		media == null
			? null
			: media.kind === "local"
				? media.audioURL
				: (media.track.playbackURL ?? null);
	const visuals = media?.visuals ?? [];
	const selectedVisual = visuals[selectedVisualIndex] ?? null;
	const lyrics = media?.kind === "local" ? media.asset.lyricsText.trim() : "";

	useEffect(() => {
		if (currentId === null) return;
		setSelectedVisualIndex(0);
		setShowLyrics(false);
		setShareStatus(null);
		setAudioError(false);
		setCurrentTime(0);
		setDuration(0);
	}, [currentId]);

	useEffect(() => {
		if (selectedVisual?.mediaType !== "video") return;
		const video = videoRef.current;
		if (video === null) return;
		if (isPlaying) void video.play().catch(() => undefined);
		else video.pause();
	}, [isPlaying, selectedVisual]);

	const openQueueItem = (item: PlayerQueueItem, resume = isPlaying) => {
		resumeAfterLoadRef.current = resume;
		audioRef.current?.pause();
		setIsPlaying(false);
		queueDialogRef.current?.close();
		router.push(playerHref(item));
	};

	useEffect(() => {
		if (
			media == null ||
			typeof navigator === "undefined" ||
			!("mediaSession" in navigator)
		) {
			return;
		}
		const session = navigator.mediaSession;
		session.metadata = new MediaMetadata({
			title,
			artist,
			album: "Artist",
			artwork: artworkURL === null ? [] : [{ src: artworkURL }],
		});
		const setHandler = (
			action: MediaSessionAction,
			handler: MediaSessionActionHandler | null,
		) => {
			try {
				session.setActionHandler(action, handler);
			} catch {
				// Media Session actions are exposed incrementally by browsers.
			}
		};
		const seekBy = (seconds: number) => {
			const audio = audioRef.current;
			if (audio === null || !Number.isFinite(audio.duration)) return;
			audio.currentTime = Math.min(
				Math.max(audio.currentTime + seconds, 0),
				audio.duration,
			);
		};
		const openItem = (item: PlayerQueueItem) => {
			resumeAfterLoadRef.current = true;
			router.push(playerHref(item));
		};
		setHandler("play", () => void audioRef.current?.play());
		setHandler("pause", () => audioRef.current?.pause());
		setHandler("stop", () => {
			const audio = audioRef.current;
			if (audio === null) return;
			audio.pause();
			audio.currentTime = 0;
		});
		setHandler("seekbackward", (details) =>
			seekBy(-(details.seekOffset ?? 10)),
		);
		setHandler("seekforward", (details) => seekBy(details.seekOffset ?? 10));
		setHandler("seekto", (details) => {
			const audio = audioRef.current;
			if (audio !== null && details.seekTime !== undefined)
				audio.currentTime = details.seekTime;
		});
		setHandler(
			"previoustrack",
			previous === undefined ? null : () => openItem(previous),
		);
		setHandler("nexttrack", next === undefined ? null : () => openItem(next));
		return () => {
			for (const action of [
				"play",
				"pause",
				"stop",
				"seekbackward",
				"seekforward",
				"seekto",
				"previoustrack",
				"nexttrack",
			] satisfies readonly MediaSessionAction[])
				setHandler(action, null);
			session.metadata = null;
			session.playbackState = "none";
		};
	}, [artist, artworkURL, media, next, previous, router, title]);

	const sharePublicTrack = async () => {
		if (media?.kind !== "public") return;
		const url = artistPublicTrackURL(runtimeConfiguration, media.track.id);
		try {
			if (navigator.share !== undefined) {
				await navigator.share({
					title: media.track.title,
					text: t("player.shareText", {
						title: media.track.title,
						artist: media.track.artist,
					}),
					url,
				});
				setShareStatus(t("player.shared"));
			} else {
				await navigator.clipboard.writeText(url);
				setShareStatus(t("player.linkCopied"));
			}
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") return;
			setShareStatus(t("player.shareFailed"));
		}
	};

	if (media === undefined) {
		return (
			<section className="player-proof-gate">
				<p role="status">{t("player.loading")}</p>
			</section>
		);
	}
	if (media === null || audioURL === null) {
		return (
			<section className="player-proof-gate">
				<h1>{t("player.missingTitle")}</h1>
				<p>{t("player.missingMessage")}</p>
				<Link className="primary-action" href="/make">
					{t("player.backToMake")}
				</Link>
			</section>
		);
	}

	const progress =
		duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
	const openDialog = (dialog: HTMLDialogElement | null) => {
		if (dialog !== null && !dialog.open) dialog.showModal();
	};

	return (
		<section
			className="playback-shared-page"
			aria-label={t("player.nowPlaying")}
		>
			<div
				className="playback-shared__backdrop"
				style={
					artworkURL === null
						? undefined
						: { backgroundImage: `url(${artworkURL})` }
				}
				aria-hidden="true"
			/>
			<div className="playback-shared__stage" data-display-mode={displayMode}>
				{selectedVisual?.mediaType === "video" ? (
					<video
						key={selectedVisual.id}
						ref={videoRef}
						className="playback-shared__stage-media"
						src={selectedVisual.url}
						muted
						loop
						playsInline
						preload="metadata"
						tabIndex={-1}
					/>
				) : selectedVisual !== null ? (
					<img
						className="playback-shared__stage-media"
						src={selectedVisual.url}
						alt=""
					/>
				) : artworkURL === null ? (
					<span
						className="playback-shared__stage-placeholder"
						aria-hidden="true"
					>
						♪
					</span>
				) : (
					<img
						className="playback-shared__stage-media"
						src={artworkURL}
						alt=""
					/>
				)}
			</div>
			<div className="playback-shared__scrim" aria-hidden="true" />

			<header className="playback-shared__header">
				<GlassIconButton
					label={t("common.back")}
					onMedia
					onClick={() => router.back()}
				>
					<ArtistIcon name="back" />
				</GlassIconButton>
				<div className="playback-shared__header-title">
					<span className="playback-shared__eyebrow">
						{t("player.nowPlaying")}
					</span>
					<strong className="playback-shared__header-track">{title}</strong>
				</div>
				<div className="playback-shared__header-actions">
					<GlassIconButton
						label={t("player.displayMode")}
						onMedia
						aria-pressed={displayMode === "fit"}
						onClick={() =>
							setDisplayMode((value) => (value === "fill" ? "fit" : "fill"))
						}
					>
						<ArtistIcon name="image" />
					</GlassIconButton>
					<GlassIconButton
						label={t("player.info")}
						onMedia
						onClick={() => openDialog(infoDialogRef.current)}
					>
						<ArtistIcon name="info" />
					</GlassIconButton>
				</div>
			</header>

			<div className="playback-shared__content">
				{showLyrics && lyrics.length > 0 ? (
					<div className="playback-shared__lyrics-scroll">
						<p className="playback-shared__lyrics-text">{lyrics}</p>
					</div>
				) : (
					<div className="playback-shared__metadata">
						<p className="playback-shared__lyric-preview">
							{lyrics.split(/\r?\n/u).find((line) => line.trim().length > 0) ??
								t("player.visualStory")}
						</p>
						<h1 className="playback-shared__title">{title}</h1>
						<p className="playback-shared__subtitle">
							<span className="playback-shared__artist">{artist}</span>
							<span className="playback-shared__dot" aria-hidden="true">
								·
							</span>
							<span>
								{media.kind === "public"
									? media.track.genre
									: t("player.localWork")}
							</span>
						</p>
					</div>
				)}

				{visuals.length > 0 ? (
					<fieldset className="playback-shared__continent-group">
						<legend className="sr-only">{t("player.visuals")}</legend>
						<div className="playback-shared__continent-list">
							{visuals.map((visual, index) => (
								<button
									key={visual.id}
									type="button"
									className="playback-shared__continent-slot"
									data-selected={selectedVisualIndex === index}
									aria-pressed={selectedVisualIndex === index}
									aria-label={t("player.visualNumber", { number: index + 1 })}
									onClick={() => setSelectedVisualIndex(index)}
								>
									{visual.mediaType === "video" ? (
										<video
											className="playback-shared__continent-image"
											src={visual.url}
											muted
											playsInline
											preload="metadata"
											tabIndex={-1}
										/>
									) : (
										<img
											className="playback-shared__continent-image"
											src={visual.url}
											alt=""
										/>
									)}
									<span className="playback-shared__continent-index">
										{index + 1}
									</span>
									{visual.mediaType === "video" ? (
										<ArtistIcon
											className="playback-shared__continent-type"
											name="play"
											size={14}
										/>
									) : null}
								</button>
							))}
						</div>
					</fieldset>
				) : null}
			</div>

			<div className="playback-shared__controls">
				<div className="playback-shared__actions">
					{lyrics.length > 0 ? (
						<button
							type="button"
							className={`playback-shared__lyrics-pill${showLyrics ? " ds-pill-button--selected" : ""}`}
							aria-pressed={showLyrics}
							onClick={() => setShowLyrics((value) => !value)}
						>
							<ArtistIcon name="doc" size={18} />
							{t("player.lyrics")}
						</button>
					) : null}
					<button
						type="button"
						className="playback-shared__lyrics-pill"
						onClick={() => openDialog(queueDialogRef.current)}
					>
						<ArtistIcon name="music" size={18} />
						{t("player.queue")}
					</button>
					<span className="playback-shared__actions-spacer" />
					{media.kind === "public" ? (
						<GlassIconButton
							label={t("player.share")}
							onMedia
							onClick={() => void sharePublicTrack()}
						>
							<ArtistIcon name="share" />
						</GlassIconButton>
					) : (
						<Link
							className="playback-shared__edit-link"
							href={`/track/edit?id=${encodeURIComponent(media.asset.id)}`}
						>
							<ArtistIcon name="sliders" size={18} />
							{t("player.edit")}
						</Link>
					)}
				</div>

				<div className="playback-shared__slider-row">
					<input
						className="playback-shared__slider"
						type="range"
						min={0}
						max={Math.max(duration, 0)}
						step={0.1}
						value={Math.min(currentTime, duration || 0)}
						aria-label={t("player.seek")}
						style={{ "--playback-progress": `${progress}%` } as CSSProperties}
						onChange={(event) => {
							const value = Number(event.currentTarget.value);
							if (audioRef.current !== null)
								audioRef.current.currentTime = value;
							setCurrentTime(value);
						}}
					/>
					<div className="playback-shared__time-row">
						<time>{formatDuration(currentTime)}</time>
						<time>-{formatDuration(Math.max(duration - currentTime, 0))}</time>
					</div>
				</div>

				<div className="playback-shared__transport">
					<GlassIconButton
						label={t("player.previous")}
						onMedia
						disabled={previous === undefined}
						onClick={() => previous !== undefined && openQueueItem(previous)}
					>
						<ArtistIcon name="back" size={26} />
					</GlassIconButton>
					<button
						type="button"
						className="playback-shared__play-button"
						aria-label={isPlaying ? t("player.pause") : t("player.play")}
						onClick={() => {
							const audio = audioRef.current;
							if (audio === null) return;
							if (audio.paused) void audio.play();
							else audio.pause();
						}}
					>
						<ArtistIcon name={isPlaying ? "pause" : "play"} size={32} />
					</button>
					<GlassIconButton
						label={t("player.next")}
						onMedia
						disabled={next === undefined}
						onClick={() => next !== undefined && openQueueItem(next)}
					>
						<ArtistIcon
							name="back"
							className="playback-shared__next-icon"
							size={26}
						/>
					</GlassIconButton>
				</div>
				{audioError ? (
					<p className="playback-shared__error" role="alert">
						{t("player.audioError")}
					</p>
				) : null}
			</div>

			{/* biome-ignore lint/a11y/useMediaCaption: this element plays music; synchronized lyrics are exposed separately when available. */}
			<audio
				ref={audioRef}
				className="playback-shared__audio"
				preload="metadata"
				src={audioURL}
				onCanPlay={(event) => {
					if (!resumeAfterLoadRef.current) return;
					resumeAfterLoadRef.current = false;
					void event.currentTarget.play();
				}}
				onPlay={() => {
					setIsPlaying(true);
					setMediaSessionPlaybackState("playing");
					if (auth.scope !== null) {
						void browserDiscoveryRepository.recordRecent(
							media.kind === "local" ? media.asset.id : media.track.id,
							auth.scope,
						);
					}
				}}
				onPause={() => {
					setIsPlaying(false);
					setMediaSessionPlaybackState("paused");
				}}
				onLoadedMetadata={(event) => {
					setDuration(
						Number.isFinite(event.currentTarget.duration)
							? event.currentTarget.duration
							: 0,
					);
					updateMediaSessionPosition(event.currentTarget);
				}}
				onDurationChange={(event) => {
					setDuration(
						Number.isFinite(event.currentTarget.duration)
							? event.currentTarget.duration
							: 0,
					);
					updateMediaSessionPosition(event.currentTarget);
				}}
				onTimeUpdate={(event) => {
					setCurrentTime(event.currentTarget.currentTime);
					updateMediaSessionPosition(event.currentTarget);
				}}
				onRateChange={(event) =>
					updateMediaSessionPosition(event.currentTarget)
				}
				onError={() => setAudioError(true)}
				onEnded={() => {
					if (next !== undefined) openQueueItem(next, true);
					else setMediaSessionPlaybackState("none");
				}}
			/>

			{shareStatus === null ? null : (
				<div className="playback-shared__notice" role="status">
					<span>{shareStatus}</span>
					<button
						type="button"
						className="playback-shared__notice-dismiss"
						onClick={() => setShareStatus(null)}
					>
						{t("common.close")}
					</button>
				</div>
			)}

			<dialog
				ref={queueDialogRef}
				className="playback-shared__dialog"
				aria-labelledby="player-queue-title"
			>
				<div className="playback-shared__dialog-header">
					<h2 id="player-queue-title">{t("player.queue")}</h2>
					<GlassIconButton
						label={t("common.close")}
						onClick={() => queueDialogRef.current?.close()}
					>
						<ArtistIcon name="close" />
					</GlassIconButton>
				</div>
				<ul className="playback-shared__sheet-list">
					{queue.map((item, index) => (
						<li key={`${item.kind}:${item.id}`}>
							<button
								type="button"
								className={`playback-shared__queue-row${item.id === currentId ? " playback-shared__queue-row--current" : ""}`}
								aria-current={item.id === currentId ? "true" : undefined}
								onClick={() => openQueueItem(item)}
							>
								<span className="playback-shared__queue-order">
									{index + 1}
								</span>
								<span className="playback-shared__queue-meta">
									<strong>{item.title}</strong>
									<span>
										{item.kind === "local"
											? t("player.localWork")
											: t("player.publicWork")}
									</span>
								</span>
								{item.id === currentId ? (
									<ArtistIcon name="waveform" size={18} />
								) : null}
							</button>
						</li>
					))}
				</ul>
			</dialog>

			<dialog
				ref={infoDialogRef}
				className="playback-shared__dialog"
				aria-labelledby="player-info-title"
			>
				<div className="playback-shared__dialog-header">
					<h2 id="player-info-title">{t("player.info")}</h2>
					<GlassIconButton
						label={t("common.close")}
						onClick={() => infoDialogRef.current?.close()}
					>
						<ArtistIcon name="close" />
					</GlassIconButton>
				</div>
				<dl className="playback-shared__info-list">
					<div>
						<dt>{t("player.track")}</dt>
						<dd>{title}</dd>
					</div>
					<div>
						<dt>{t("player.artist")}</dt>
						<dd>{artist}</dd>
					</div>
					<div>
						<dt>{t("player.duration")}</dt>
						<dd>
							{formatDuration(
								duration ||
									(media.kind === "public"
										? media.track.durationSeconds
										: media.asset.durationSeconds),
							)}
						</dd>
					</div>
					<div>
						<dt>{t("player.visuals")}</dt>
						<dd>{visuals.length}</dd>
					</div>
				</dl>
			</dialog>
		</section>
	);
}

function playerHref(item: PlayerQueueItem): Route {
	const key = item.kind === "local" ? "id" : "remote";
	return `/player?${key}=${encodeURIComponent(item.id)}` as Route;
}

function formatDuration(value: number): string {
	if (!Number.isFinite(value) || value <= 0) return "0:00";
	const seconds = Math.floor(value);
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function setMediaSessionPlaybackState(state: MediaSessionPlaybackState): void {
	if (typeof navigator === "undefined" || !("mediaSession" in navigator))
		return;
	navigator.mediaSession.playbackState = state;
}

function updateMediaSessionPosition(audio: HTMLAudioElement): void {
	if (typeof navigator === "undefined" || !("mediaSession" in navigator))
		return;
	if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
	try {
		navigator.mediaSession.setPositionState({
			duration: audio.duration,
			playbackRate: audio.playbackRate,
			position: Math.min(Math.max(audio.currentTime, 0), audio.duration),
		});
	} catch {
		// Ignore transient metadata while the browser changes tracks.
	}
}
